import { useState, useCallback } from 'react';
import { HomePage } from './pages/HomePage';
import { SearchPage } from './pages/SearchPage';
import { ChatbotPage } from './pages/ChatbotPage';
import { ConversationSidebar } from './components/ConversationSidebar';
import { GovBar } from './components/GovBar';
import { InstitutionalHeader } from './components/InstitutionalHeader';
import { GovFooter } from './components/GovFooter';
import { ApiService } from './services/api.service';
import { getUsuarioActual } from './auth/usuario';
import type { ConversacionSummary, Message } from './types/api.types';
import './index.css';

type Page = 'home' | 'search' | 'chatbot';

interface ConversationsState {
  list: ConversacionSummary[];
  activeId: string | null;
  messages: Message[];
  isLoading: boolean;
  chatLoading: boolean;
  chatError: string | null;
}

export function App() {
  const [page, setPage] = useState<Page>('home');
  // Toda la app se monta detrás del SSO, así que el usuario ya está resuelto:
  // se lee de los claims del token, no de un estado propio (ver src/auth/usuario.ts).
  const usuario = getUsuarioActual();
  const [conversations, setConversations] = useState<ConversationsState>({
    list: [],
    activeId: null,
    messages: [],
    isLoading: false,
    chatLoading: false,
    chatError: null,
  });

  const loadConversations = useCallback(async () => {
    setConversations(prev => ({ ...prev, isLoading: true }));
    try {
      const list = await ApiService.getConversaciones();
      setConversations(prev => ({ ...prev, list, isLoading: false }));
    } catch {
      setConversations(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const navigate = useCallback((p: string) => {
    if (p === 'chatbot') {
      setPage('chatbot');
      loadConversations();
    } else {
      setPage(p as Page);
    }
  }, [loadConversations]);

  const handleSelectConversation = useCallback(async (id: string) => {
    setConversations(prev => ({ ...prev, activeId: id, messages: [], chatError: null }));
    try {
      const detail = await ApiService.getConversacion(id);
      const messages: Message[] = detail.mensajes.map(m => ({
        id: m.id,
        role: m.rol,
        content: m.contenido,
      }));
      setConversations(prev => ({ ...prev, messages }));
    } catch (err) {
      if (err instanceof Error && err.message.includes('404')) {
        setConversations(prev => ({
          ...prev,
          list: prev.list.filter(c => c.id !== id),
          activeId: null,
          messages: [],
        }));
      }
    }
  }, []);

  const handleCreateConversation = useCallback(async (): Promise<string | null> => {
    try {
      const detail = await ApiService.createConversacion();
      const summary: ConversacionSummary = {
        id: detail.id,
        titulo: detail.titulo,
        created_at: detail.created_at,
        updated_at: detail.updated_at,
        total_mensajes: 0,
      };
      setConversations(prev => ({
        ...prev,
        list: [summary, ...prev.list],
        activeId: detail.id,
        messages: [],
        chatError: null,
      }));
      return detail.id;
    } catch {
      return null;
    }
  }, []);

  const handleRenameConversation = useCallback(async (id: string, titulo: string) => {
    try {
      const updated = await ApiService.renameConversacion(id, titulo);
      setConversations(prev => ({
        ...prev,
        list: prev.list.map(c => c.id === id ? updated : c),
      }));
    } catch {
      // UI already reverted title in ConversationItem
    }
  }, []);

  const handleDeleteConversation = useCallback(async (id: string) => {
    try {
      await ApiService.deleteConversacion(id);
    } catch {
      // 404 = already gone
    }
    setConversations(prev => ({
      ...prev,
      list: prev.list.filter(c => c.id !== id),
      activeId: prev.activeId === id ? null : prev.activeId,
      messages: prev.activeId === id ? [] : prev.messages,
    }));
  }, []);

  const handleSendMessage = useCallback(async (query: string) => {
    let convId = conversations.activeId;

    if (!convId) {
      const newId = await handleCreateConversation();
      if (!newId) return;
      convId = newId;
    }

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: query,
    };

    setConversations(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      chatLoading: true,
      chatError: null,
    }));

    try {
      const response = await ApiService.chatbot({
        query,
        conversation_id: convId,
        top_k: 5,
        temperature: 0.0,
      });

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.response,
        sources: response.sources,
        model: response.model,
        tokens_used: response.tokens_used,
      };

      setConversations(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage],
        chatLoading: false,
        list: prev.list.map(c =>
          c.id === convId
            ? { ...c, updated_at: new Date().toISOString(), total_mensajes: c.total_mensajes + 2 }
            : c
        ),
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al procesar la consulta';
      setConversations(prev => ({ ...prev, chatLoading: false, chatError: msg }));
    }
  }, [conversations.activeId, handleCreateConversation]);

  const handleNewConversation = useCallback(async () => {
    await handleCreateConversation();
  }, [handleCreateConversation]);

  return (
    <div className="flex flex-col min-h-screen">
      <a
        href="#contenido-principal"
        className="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:bg-white focus:text-[#0943B5] focus:px-4 focus:py-2 focus:font-semibold"
      >
        Saltar al contenido principal
      </a>
      <GovBar />
      <InstitutionalHeader />
      <main id="contenido-principal" className="flex-1 flex flex-col">
        {page === 'home' && <HomePage onNavigate={navigate} />}
        {page === 'search' && <SearchPage onNavigate={navigate} />}
        {page === 'chatbot' && (
          <div className="flex flex-1 flex-col h-0">
            {/* Full-width chatbot header — acts as third nav level */}
            <header className="border-b border-gray-200 bg-white px-4 sm:px-6 py-4 shadow-sm flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => navigate('home')}
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Inicio
                  </button>
                  <div className="w-px h-5 bg-gray-200" />
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-[#0943B5] rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Consulta Avanzada</p>
                      <p className="text-xs text-gray-400">Asistente de IA · Biblioteca PQR</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {conversations.activeId && (
                    <span className="hidden sm:flex items-center gap-1.5 text-xs text-green-600 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      Conversación activa
                    </span>
                  )}
                </div>
              </div>
            </header>

            {/* Sidebar + chat content below the full-width header */}
            <div className="flex flex-1 overflow-hidden">
              <ConversationSidebar
                conversations={conversations.list}
                activeId={conversations.activeId}
                onSelect={handleSelectConversation}
                onCreate={handleNewConversation}
                onRename={handleRenameConversation}
                onDelete={handleDeleteConversation}
                userEmail={usuario.email ?? usuario.username}
                isLoading={conversations.isLoading}
              />
              <div className="flex-1 overflow-hidden flex flex-col min-w-0">
                <ChatbotPage
                  messages={conversations.messages}
                  isLoading={conversations.chatLoading}
                  error={conversations.chatError}
                  onSendMessage={handleSendMessage}
                />
              </div>
            </div>
          </div>
        )}
      </main>
      <GovFooter />
    </div>
  );
}

export default App;
